
import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';
import { calculateMortgagePayment } from '../utils/mortgageUtils';
import type { MortgageData } from '../../CalculatorTabs';

interface EquityGrowthTabProps {
  selectedTerm: string;
  mortgageData: MortgageData;
}

export const EquityGrowthTab: React.FC<EquityGrowthTabProps> = ({ selectedTerm, mortgageData }) => {
  const equityGrowthData = useMemo(() => {
    const data = [];
    const { homePrice, downPayment, interestRate } = mortgageData;
    const loanAmount = homePrice - downPayment;
    const appreciationRate = 0.03; // 3% annual appreciation
    const termYears = parseInt(selectedTerm);
    const monthlyPayment = calculateMortgagePayment(loanAmount, interestRate, termYears);
    const monthlyRate = interestRate / 100 / 12;
    
    let currentValue = homePrice;
    let remainingLoan = loanAmount;
    
    const stepSize = Math.max(2, Math.floor(termYears / 15)); // Adjust step size based on term
    
    for (let year = stepSize; year <= termYears; year += stepSize) {
      currentValue *= Math.pow(1 + appreciationRate, stepSize);
      
      // Calculate remaining loan balance after payments
      for (let months = 0; months < stepSize * 12; months++) {
        if (remainingLoan <= 0) break;
        const interestPayment = remainingLoan * monthlyRate;
        const principalPayment = Math.min(monthlyPayment - interestPayment, remainingLoan);
        remainingLoan -= principalPayment;
      }
      
      data.push({
        year: `Year ${year}`,
        homeValue: Math.round(currentValue),
        loanBalance: Math.max(0, Math.round(remainingLoan)),
        equity: Math.round(currentValue - Math.max(0, remainingLoan))
      });
    }
    
    return data;
  }, [selectedTerm, mortgageData]);

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6">
      <h3 className="text-lg font-bold text-slate-100 mb-4">
        Equity Growth Projection ({selectedTerm} Year Term)
      </h3>
      <div className="w-full h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={equityGrowthData}>
            <XAxis 
              dataKey="year" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              tickFormatter={(value) => `$${(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={{ 
                backgroundColor: '#0f172a', 
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#e2e8f0'
              }}
              formatter={(value, name) => {
                const label = name === 'homeValue' ? 'Home Value' : 
                             name === 'loanBalance' ? 'Loan Balance' : 'Equity';
                return [`$${Number(value).toLocaleString()}`, label];
              }}
            />
            <Line 
              type="monotone" 
              dataKey="homeValue" 
              stroke="#8b5cf6" 
              strokeWidth={3}
              dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="loanBalance" 
              stroke="#ef4444" 
              strokeWidth={3}
              strokeDasharray="5 5"
              dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="equity" 
              stroke="#10b981" 
              strokeWidth={3}
              dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
